import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { form } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { AjastettuTyo, AjastettuTyoService, AjastetunTyonTyyppi } from '@lemon/core-firebase/eraajot/ajastettu/ajastetut-tyot.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { combineLatest, map, Subject, switchMap, takeUntil, tap } from 'rxjs'
import { AjastetutTyotDataDialog } from './ajastetut-tyot-data.dialog'

interface AjastetutTyotLomake {
  tyyppi: string | null
  tulossaVaiHistoria: 'tulossa' | 'historia'
}

@Component({
  templateUrl: './yllapito-ajastetut-tyot.component.html',
  styleUrls: ['./yllapito-ajastetut-tyot.component.css'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class YllapitoAjastetutTyotComponent implements OnInit, OnDestroy, AfterViewInit {
  naytettavatKolumnit: string[] = ['linkki', 'asiakasAvain', 'ajoaika', 'selite']

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  lomake = form(signal<AjastetutTyotLomake>({ tyyppi: null, tulossaVaiHistoria: 'tulossa' }))
  private readonly _injector = inject(Injector)
  private readonly _changeDetectorRef = inject(ChangeDetectorRef)

  @ViewChild(MatSort, { static: true }) _sort: MatSort
  @ViewChild(MatPaginator, { static: true }) _paginator: MatPaginator

  dataSource = new MatTableDataSource<AjastettuTyo<any>>([])
  private _kirjanpitajaOnDevaaja: boolean

  ajastetunTyonTyyppi: { tyyppi: AjastetunTyonTyyppi; teksti: string }[] = []

  constructor(
    private _firebaseLemonator: FirebaseLemonator,
    private _ladataanService: LadataanService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _dialog: MatDialog,
    private _ajastettuTyoService: AjastettuTyoService,
  ) {}

  ngOnInit() {
    this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(onkoDevaaja => {
      this._kirjanpitajaOnDevaaja = onkoDevaaja
    })

    this.ajastetunTyonTyyppi = this._ajastettuTyoService.getAjastetunTyonTyypit()

    const tyyppiKentta = this.lomake.tyyppi
    const tulossaVaiHistoriaKentta = this.lomake.tulossaVaiHistoria

    combineLatest([arvonMuutokset(tyyppiKentta, { injector: this._injector }), arvonMuutokset(tulossaVaiHistoriaKentta, { injector: this._injector })])
      .pipe(
        tap(() => this._ladataanService.aloitaLataaminen()),
        switchMap(([tyyppi, tulossaVaiHistoria]) => {
          if (!tyyppi || !tulossaVaiHistoria) {
            return []
          }
          const collection = tulossaVaiHistoria === 'tulossa' ? this._ajastettuTyoService.collectionName : this._ajastettuTyoService.collectionNameHistory
          return this._firebaseLemonator
            .firestoreCollection<AjastettuTyo<any>>(collection)
            .orderBy('ajoaika', 'desc')
            .limit(1000)
            .listen()
            .pipe(
              map(tyot => {
                // Overwrite selite field with standard explanation
                for (const tyo of tyot) {
                  tyo.selite = this.ajastetunTyonTyyppi.find(t => t.tyyppi === tyo.tyyppi)?.teksti ?? tyo.selite
                }

                if (tyyppi && tyyppi !== 'all') {
                  return tyot.filter(tyo => tyo.tyyppi === tyyppi)
                }
                return tyot
              }),
            )
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(tyot => {
        this.dataSource.data = tyot
        this.dataSource.paginator = this._paginator
        this._ladataanService.lopetaLataaminen()
        this._changeDetectorRef.markForCheck()
      })

    asetaArvo(tyyppiKentta, 'all')
    asetaArvo(tulossaVaiHistoriaKentta, 'tulossa')
  }

  ngAfterViewInit() {
    this.dataSource.sort = this._sort
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  katsoTiedot(rivi: AjastettuTyo<any>) {
    if (this._kirjanpitajaOnDevaaja) {
      this._dialog.open(AjastetutTyotDataDialog, { data: rivi })
    }
  }

  openLink(asiakasAvain: string, event: Event) {
    event.preventDefault()
    event.stopPropagation()

    window.open('https://lemonator.lemontree.fi/asiakkaat/' + asiakasAvain + '/asiakastiedot', '_blank')
  }
}
